/**
 * Recurring documents — the template `payload` is EXACTLY the create wire
 * body minus its date field(s). The scheduler (`recurring_generate_for_template`)
 * reads the payload keys by name and calls the same four create RPCs the
 * routes call today, supplying the occurrence date itself.
 *
 *  - `buildRecurringPayload(type, body)` — strips precisely
 *      expense    → `expense_date`
 *      ap_bill    → `invoice_date` + `due_date`   (keeps `invoice_number`)
 *      ar_invoice → `invoice_date` + `due_date` + `supply_date` (each
 *                   occurrence supplies on its own date — 20261101120000)
 *      journal    → `transaction_date`
 *    from a body produced by the matching `build*CreateBody` export
 *    (`useExpenses` / `useAccountsPayable` / `useAccountsReceivable` /
 *    `useLedgerTransactions`), so the mutation and the template payload come
 *    from ONE function. Nothing else is touched — `recurringPayloadSchemaByType`
 *    (`.strict()`, @releve/shared) is what the route validates against, and
 *    `recurringPayload.test.ts` proves every fixture payload round-trips.
 *
 *  - `hydrate*FromPayload(payload)` — template-EDIT mode: a saved payload back
 *    into the field shape each create form `reset()`s with. These mirror the
 *    forms' own edit-mode hydration (`CreateExpenseForm.tsx` ~`:735`,
 *    `CreateAPInvoiceForm.tsx` ~`:873`, `CreateARInvoiceForm.tsx` ~`:534`,
 *    `CreateTransactionModal.tsx` ~`:333`) field for field, so a template
 *    opens looking like the document it will generate. The return shapes are
 *    declared here as plain interfaces (structurally the forms' `FormData`
 *    minus the date + recurring fields) — the forms spread them into their
 *    `defaultValues` / `reset(values)`.
 */
import type {
  RecurringApBillPayload,
  RecurringArInvoicePayload,
  RecurringDocumentType,
  RecurringExpensePayload,
  RecurringJournalPayload,
  RecurringTemplatePayloadByType,
} from '@releve/shared';
import { toSalesTaxRateType, toTaxRateType, type SalesTaxRateType, type TaxRateType } from '@/lib/taxCalculations';

// ---------------------------------------------------------------------------
// Strip
// ---------------------------------------------------------------------------

/** The date key(s) the generator supplies per type — the ONLY keys stripped. */
export const RECURRING_DATE_KEYS_BY_TYPE = {
  expense: ['expense_date'],
  ap_bill: ['invoice_date', 'due_date'],
  ar_invoice: ['invoice_date', 'due_date', 'supply_date'],
  journal: ['transaction_date'],
} as const satisfies Record<RecurringDocumentType, readonly string[]>;

/** The create wire body per type = the payload plus its date key(s). */
export interface RecurringCreateBodyByType {
  expense: RecurringExpensePayload & { expense_date: string };
  ap_bill: RecurringApBillPayload & { invoice_date: string; due_date: string };
  ar_invoice: RecurringArInvoicePayload & {
    invoice_date: string;
    due_date: string;
    /** The draft's supply date — never on a template (the occurrence date is the supply). */
    supply_date?: string | null;
  };
  journal: RecurringJournalPayload & { transaction_date: string };
}

/**
 * Create wire body → template payload. Pure key removal; the body's other
 * keys pass through byte-identical (no `undefined` pruning — JSON drops them
 * on the wire exactly as the create POST does).
 */
export function buildRecurringPayload<T extends RecurringDocumentType>(
  documentType: T,
  body: RecurringCreateBodyByType[T],
): RecurringTemplatePayloadByType[T] {
  const strip: readonly string[] = RECURRING_DATE_KEYS_BY_TYPE[documentType];
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (!strip.includes(key)) payload[key] = value;
  }
  // The cast is the type-level statement of the JSDoc above: the body is the
  // payload plus the stripped date keys, and the fixtures test asserts it.
  return payload as RecurringTemplatePayloadByType[T];
}

// ---------------------------------------------------------------------------
// Hydrate (template-edit mode)
// ---------------------------------------------------------------------------

/** '' where the forms use '' for "unset" (Select/SearchableSelect values). */
const orEmpty = (value: string | null | undefined): string => value ?? '';

/** One expense/AP line as the two forms' `line_items[]` hold it. */
export interface HydratedPurchaseLineItem {
  expense_account_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: TaxRateType;
  tax_rate_id: string;
  tax_inclusive: boolean;
  is_reverse_charge: boolean;
  exemption_reason: string;
  exemption_reason_other: string;
  wkr_enabled: boolean;
  wkr_category: string;
}

function hydratePurchaseLine(line: {
  expense_account_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_rate_id?: string | null;
  tax_treatment?: string | null;
  tax_inclusive?: boolean | null;
  exemption_reason?: string | null;
  exemption_reason_other?: string | null;
  wkr_category?: string | null;
}): HydratedPurchaseLineItem {
  const { taxRate, isReverseCharge } = toTaxRateType(line.tax_treatment, line.tax_rate ?? 0);
  return {
    expense_account_id: line.expense_account_id,
    description: line.description,
    quantity: line.quantity,
    unit_price: line.unit_price,
    tax_rate: taxRate,
    tax_rate_id: orEmpty(line.tax_rate_id),
    tax_inclusive: line.tax_inclusive ?? false,
    is_reverse_charge: isReverseCharge,
    exemption_reason: orEmpty(line.exemption_reason),
    exemption_reason_other: orEmpty(line.exemption_reason_other),
    wkr_enabled: !!line.wkr_category,
    wkr_category: orEmpty(line.wkr_category),
  };
}

/** `CreateExpenseForm`'s fields a payload can fill (no `expense_date`). */
export interface HydratedExpenseFormValues {
  payee_type: 'business' | 'person';
  business_id: string;
  business_name: string;
  person_id: string;
  reference: string;
  memo: string;
  payment_account_id: string;
  project_id: string;
  fund_id: string;
  line_items: HydratedPurchaseLineItem[];
}

export function hydrateExpenseFromPayload(payload: RecurringExpensePayload): HydratedExpenseFormValues {
  // Mirrors the form's edit hydration: a linked business shows through the
  // SearchableSelect by id; a free-text payee keeps its text as the "custom"
  // value; a person is the other radio.
  const isBusiness = !!payload.business_id || !!payload.custom_business_name;
  return {
    payee_type: isBusiness ? 'business' : 'person',
    business_id: orEmpty(payload.business_id),
    business_name: payload.business_id ?? payload.custom_business_name ?? '',
    person_id: orEmpty(payload.person_id),
    reference: orEmpty(payload.reference),
    memo: payload.description ?? '',
    payment_account_id: payload.payment_account_id,
    project_id: orEmpty(payload.project_id),
    fund_id: orEmpty(payload.fund_id),
    line_items: payload.line_items.map(hydratePurchaseLine),
  };
}

/** `CreateAPInvoiceForm`'s fields a payload can fill (no `invoice_date`/`due_date`). */
export interface HydratedAPBillFormValues {
  payee_type: 'business' | 'person';
  business_id: string;
  business_name: string;
  person_id: string;
  invoice_number: string;
  /** The form's terms select — a template carries `due_days` instead, so 'custom'. */
  payment_terms: string;
  memo: string;
  /** The form's "reference" field IS `purchase_order_number` on the wire. */
  reference: string;
  ap_account_id: string;
  project_id: string;
  fund_id: string;
  line_items: HydratedPurchaseLineItem[];
}

export function hydrateAPBillFromPayload(payload: RecurringApBillPayload): HydratedAPBillFormValues {
  return {
    payee_type: payload.business_id ? 'business' : 'person',
    business_id: orEmpty(payload.business_id),
    // SearchableSelect displays the label for a uuid value.
    business_name: orEmpty(payload.business_id),
    person_id: orEmpty(payload.person_id),
    invoice_number: payload.invoice_number,
    payment_terms: 'custom',
    memo: payload.description ?? '',
    reference: orEmpty(payload.purchase_order_number),
    ap_account_id: payload.ap_account_id,
    project_id: orEmpty(payload.project_id),
    fund_id: orEmpty(payload.fund_id),
    line_items: payload.line_items.map(hydratePurchaseLine),
  };
}

/** One AR line as `CreateARInvoiceForm` holds it (no WKR on AR). */
export interface HydratedARLineItem {
  service_category_id: string;
  revenue_account_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: SalesTaxRateType;
  tax_rate_id: string;
  tax_inclusive: boolean;
  is_reverse_charge: boolean;
  exemption_reason: string;
  exemption_reason_other: string;
}

/** `CreateARInvoiceForm`'s fields a payload can fill (no `invoice_date`/`due_date`). */
export interface HydratedARInvoiceFormValues {
  customer_type: 'business' | 'person';
  business_id: string;
  /** The business id when linked, else the free-text `customer_name`. */
  business_name: string;
  person_id: string;
  payment_terms: string;
  memo: string;
  reference: string;
  ar_account_id: string;
  project_id: string;
  fund_id: string;
  customer_address: string;
  line_items: HydratedARLineItem[];
}

export function hydrateARInvoiceFromPayload(payload: RecurringArInvoicePayload): HydratedARInvoiceFormValues {
  // Same rule as the form's edit hydration: a person wins the radio; a
  // business shows by id, or the typed customer name when it was custom.
  const isPerson = !!payload.person_id;
  const businessName = payload.business_id ?? (!isPerson ? payload.customer_name : '');
  return {
    customer_type: isPerson ? 'person' : 'business',
    business_id: orEmpty(payload.business_id),
    business_name: businessName,
    person_id: orEmpty(payload.person_id),
    payment_terms: 'custom',
    memo: payload.description ?? '',
    reference: orEmpty(payload.reference),
    ar_account_id: payload.ar_account_id,
    project_id: orEmpty(payload.project_id),
    fund_id: orEmpty(payload.fund_id),
    customer_address: payload.customer_address ?? '',
    line_items: payload.line_items.map((line) => {
      const { taxRate, isReverseCharge } = toSalesTaxRateType(line.tax_treatment, line.tax_rate ?? 0);
      return {
        service_category_id: orEmpty(line.service_category_id),
        revenue_account_id: line.revenue_account_id,
        description: line.description,
        quantity: line.quantity,
        unit_price: line.unit_price,
        tax_rate: taxRate,
        tax_rate_id: orEmpty(line.tax_rate_id),
        tax_inclusive: line.tax_inclusive ?? false,
        is_reverse_charge: isReverseCharge,
        exemption_reason: orEmpty(line.exemption_reason),
        exemption_reason_other: orEmpty(line.exemption_reason_other),
      };
    }),
  };
}

/**
 * `CreateTransactionModal`'s explicit "no fund" line marker — `''` there
 * means "inherit the header fund". Same literal as the modal's private
 * constant; equality is by value so the two need not share an import.
 */
export const JOURNAL_NO_FUND_SENTINEL = '__no_fund__';

export interface HydratedJournalEntry {
  account_id: string;
  fund_id: string;
  debit_amount: number;
  credit_amount: number;
  description: string;
  wkr_category: string;
}

/** `CreateTransactionModal`'s `TransactionFormData` minus `transaction_date`. */
export interface HydratedJournalFormValues {
  description: string;
  total_amount: number;
  reference: string;
  project_id: string;
  business_id: string;
  fund_id: string;
  entries: HydratedJournalEntry[];
}

export function hydrateJournalFromPayload(payload: RecurringJournalPayload): HydratedJournalFormValues {
  const headerFund = payload.fund_id ?? null;
  return {
    description: payload.description,
    total_amount: payload.total_amount,
    reference: orEmpty(payload.reference),
    project_id: orEmpty(payload.project_id),
    business_id: orEmpty(payload.business_id),
    fund_id: orEmpty(headerFund),
    entries: payload.entries.map((entry) => ({
      account_id: entry.account_id,
      // The modal's match-header rule: a line fund equal to the header's
      // hydrates as '' (inherit) so header edits keep propagating; a
      // different fund is an explicit override; NULL under a header fund is
      // an explicit no-fund; with no header fund there is nothing to
      // inherit, so '' / the line's own fund.
      fund_id: entry.fund_id
        ? entry.fund_id === headerFund
          ? ''
          : entry.fund_id
        : headerFund
          ? JOURNAL_NO_FUND_SENTINEL
          : '',
      debit_amount: entry.debit_amount ?? 0,
      credit_amount: entry.credit_amount ?? 0,
      description: orEmpty(entry.description),
      wkr_category: orEmpty(entry.wkr_category),
    })),
  };
}
